import { Chip } from '@hologram-dimension/chip';
import Field from 'components/Field/Field';
import { FC, useMemo } from 'react';
import useAlgoliaFilter from 'components/CoverageSearch/useAlgoliaFilter';
import useAlgoliaMeta from 'components/CoverageSearch/useAlgoliaMeta';
import formatMoney from 'utils/numbers/formatMoney';
import useBillingRates from 'api/billing/useBillingRates';
import { Icon } from '@hologram-dimension/icon';
import usePlan, { PlanResult } from 'api/legacy/plans/usePlan';
import useOrganization from 'state/contexts/Organization/useOrganization';
import usePlans from 'api/legacy/organization/usePlans';
import { calculateRateRange } from 'components/CoverageSearch/searchUtils';
import styles from './PlanCoveragePanelHeader.module.css';

interface PlanCoveragePanelHeaderProps {
  planId: PlanId;
  zone: string;
}

const Profiles: FC<{ profiles?: NonNullable<PlanResult['data']>['profiles'] }> = ({ profiles }) => (
  <div className={styles.profiles}>
    {profiles?.map((profile) => (
      <Chip key={profile.offerId} size="medium">
        {profile.name}
      </Chip>
    ))}
  </div>
);

const PlanCoveragePanelHeader: FC<PlanCoveragePanelHeaderProps> = ({ planId, zone }) => {
  const organizationId = useOrganization();
  const { plans, isLoading: isPlansLoading, error: plansError } = usePlans({ organizationId });
  const plan = plans.find((p) => p.id === planId && p.zone === zone);
  const { plan: legacyPlan, isLoading: isPlanLoading, error: planError } = usePlan(planId);
  const searchFilter = useAlgoliaFilter({ planProfiles: legacyPlan?.profiles });
  const { data: meta, error: metaError, isLoading: isMetaLoading } = useAlgoliaMeta(searchFilter);
  const {
    billingRates,
    error: billingRatesError,
    isLoading: isBillingRatesLoading,
  } = useBillingRates(organizationId);

  const rateRange = useMemo(
    () => calculateRateRange(billingRates, meta, plan?.overageCharge),
    [billingRates, meta, plan?.overageCharge]
  );

  const rateRangeContent = useMemo(() => {
    if (isBillingRatesLoading || isMetaLoading || isPlanLoading || isPlansLoading) {
      return <span className={styles.diminished}>Loading&hellip;</span>;
    }
    if (billingRatesError || metaError || planError || plansError)
      return (
        <span className={styles.diminished}>
          <Icon name="Warning" size="small" fill="DdsColorFillNotificationError" /> Unable to load
        </span>
      );
    if (rateRange.isFlatRate) {
      return `${formatMoney(rateRange.rateMin)}`;
    }
    return `${formatMoney(rateRange.rateMin)} – ${formatMoney(rateRange.rateMax)}`;
  }, [
    isBillingRatesLoading,
    isMetaLoading,
    isPlanLoading,
    isPlansLoading,
    billingRatesError,
    metaError,
    planError,
    plansError,
    rateRange.isFlatRate,
    rateRange.rateMin,
    rateRange.rateMax,
  ]);

  const error = metaError || billingRatesError || planError || plansError;

  if (error) return null; // For now, since this is non-critical information

  return (
    <div className={styles.fields}>
      <Field
        direction="column"
        label="Networks"
        value={
          isMetaLoading ? (
            <span className={styles.diminished}>Loading&hellip;</span>
          ) : (
            meta?.networks
          )
        }
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label="Countries"
        value={
          isMetaLoading ? (
            <span className={styles.diminished}>Loading&hellip;</span>
          ) : (
            meta?.countries
          )
        }
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label={plan?.dataLimit === 0 ? 'Usage per MB' : 'Overage per MB'}
        value={rateRangeContent}
        valueClassname={styles.fieldValue}
      />
      <Field
        direction="column"
        label="Available profiles"
        value={<Profiles profiles={legacyPlan?.profiles} />}
      />
    </div>
  );
};

export default PlanCoveragePanelHeader;
